import type { UserRole } from '@inou/shared';
import { useState } from 'react';

import { PageHeader } from '@/components/layout/PageHeader';
import { NavAction } from '@/components/nav/EntityLink';
import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { Select } from '@/components/ui/select';
import { Stat, StatRow } from '@/components/ui/stat';
import { Table, TableWrap, Td, TdNum, Th } from '@/components/ui/table';
import {
  ApiError,
  api,
  type Invitation,
  type Member,
  type SeatUsage,
  type TenantSettings,
} from '@/lib/api';
import { yenExact } from '@/lib/format';
import { toAssignments, toTimeLogs, useQueryParam, useSelectedParam } from '@/lib/nav';
import { useApi } from '@/lib/useApi';

const ROLE_LABEL: Record<string, string> = {
  admin: '管理者',
  member: 'メンバー',
  mcp: 'MCP Connect',
};

/**
 * 権限の選択肢。消費する枠が種別ごとに違うので、残席が 0 の種別は選べなくする
 * (保存してからサーバーに 402 で弾かれるより、選ぶ前に分かるほうがよい)。
 */
const ROLE_OPTIONS: Array<{ value: UserRole; label: string; license: 'mcp' | 'full' }> = [
  { value: 'mcp', label: 'MCP Connect (MCP と自分の工数のみ)', license: 'mcp' },
  { value: 'member', label: 'メンバー (管理画面フル)', license: 'full' },
  { value: 'admin', label: '管理者 (マスタ管理まで)', license: 'full' },
];

function RoleOptions({ seats, current }: { seats?: SeatUsage; current?: UserRole }) {
  return (
    <>
      {ROLE_OPTIONS.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={
            // 今のロールから動かさない場合は枠を食い直さないので、常に選べる
            option.value !== current &&
            seats !== undefined &&
            seats[option.license].remainingSeats === 0
          }
        >
          {option.label}
          {seats && option.value !== current
            ? ` — 残り ${seats[option.license].remainingSeats} 席`
            : ''}
        </option>
      ))}
    </>
  );
}

/**
 * メンバー管理。
 *
 * 招待はシートを消費し、招待リンクは発行時にしか手に入らない。
 * メール送信の口をまだ持っていないので、管理者がリンクを本人に渡す。
 */
export function MembersPage() {
  const { data, error, loading, reload } = useApi<{ users: Member[]; seats: SeatUsage }>(
    '/api/users',
  );
  const settings = useApi<{ tenant: TenantSettings }>('/api/tenant/settings');
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<{ email: string; invitation: Invitation } | null>(null);
  // 開いている行と絞り込みは URL が持つ。稼働レポートやアサイン計画から
  // 「この人」を開けるようにするため (?user=<id>)。
  const [editing, setEditing] = useSelectedParam('user');
  const [query, setQuery] = useQueryParam();

  const members = (data?.users ?? []).filter((member) =>
    matchesQuery(query, member.name, member.email),
  );
  const seats = data?.seats;
  const defaults = settings.data?.tenant;

  return (
    <>
      <PageHeader
        title="メンバー"
        subtitle={
          data
            ? query.trim() === ''
              ? `${data.users.length} 名`
              : `${members.length} / ${data.users.length} 名`
            : undefined
        }
        actions={
          <Button variant="primary" onClick={() => setInviting((value) => !value)}>
            {inviting ? '閉じる' : 'メンバーを招待'}
          </Button>
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        {seats ? (
          <StatRow className="grid-cols-2">
            <Stat
              label="Full Management (利用中 / 枠)"
              value={`${seats.full.activeUsers} / ${seats.full.availableSeats}`}
              hint={`残り ${seats.full.remainingSeats} 席`}
              tone={seats.full.remainingSeats === 0 ? 'alert' : 'brand'}
            />
            <Stat
              label="MCP Connect (利用中 / 枠)"
              value={`${seats.mcp.activeUsers} / ${seats.mcp.availableSeats}`}
              hint={`残り ${seats.mcp.remainingSeats} 席`}
              tone={seats.mcp.remainingSeats === 0 ? 'alert' : 'default'}
            />
          </StatRow>
        ) : null}

        {inviting ? (
          <InviteForm
            seats={seats}
            onInvited={(result) => {
              setIssued(result);
              setInviting(false);
              reload();
            }}
          />
        ) : null}

        {issued ? (
          <InvitationLink
            email={issued.email}
            invitation={issued.invitation}
            onDismiss={() => setIssued(null)}
          />
        ) : null}

        <StateBlock
          loading={loading}
          error={error}
          empty={data?.users.length === 0}
          emptyMessage="メンバーがいません。"
        >
          <div className="border border-line bg-surface-raised">
            <div className="border-b border-line px-4 py-3">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="名前・メールアドレスで絞り込む"
                className="w-full md:w-80"
              />
            </div>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>MEMBER</Th>
                    <Th>ROLE</Th>
                    <Th>STATUS</Th>
                    <Th className="text-right">COST / h</Th>
                    <Th className="text-right">RATE / h</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <MemberRow
                      key={member.id}
                      member={member}
                      seats={seats}
                      defaults={defaults ?? null}
                      expanded={editing === member.id}
                      onToggle={() => setEditing(editing === member.id ? null : member.id)}
                      onSaved={reload}
                      onReissued={(invitation) => setIssued({ email: member.email, invitation })}
                    />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {members.length === 0 ? (
              <p className="border-t border-line px-4 py-8 text-sm text-ink-subtle">
                「{query}」に一致するメンバーはいません。
              </p>
            ) : null}
          </div>
        </StateBlock>

        <p className="text-xs leading-relaxed text-ink-subtle">
          単価を空欄にすると、テナントの既定単価（設定画面）が使われます。
          単価を変えても記録済みの工数は書き換わりませんが、集計時に解決されるため
          過去月のレポートの金額は変わります。
          退職者は無効化してください（削除すると過去の集計から人が消えます）。
        </p>
      </div>
    </>
  );
}

function InviteForm({
  seats,
  onInvited,
}: {
  seats: SeatUsage | undefined;
  onInvited: (result: { email: string; invitation: Invitation }) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ invitation: Invitation }>('/api/users', {
        email: email.trim(),
        name: name.trim(),
        role,
      });
      onInvited({ email: email.trim(), invitation: result.invitation });
      setEmail('');
      setName('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <h2 className="text-sm font-bold">メンバーを招待する</h2>
      <FieldRow>
        <Field label="メールアドレス" className="min-w-64 flex-1">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="dev@example.com"
          />
        </Field>
        <Field label="表示名" className="min-w-48">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="開発 花子" />
        </Field>
        <Field label="権限" className="min-w-64">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <RoleOptions seats={seats} />
          </Select>
        </Field>
        <Button
          variant="primary"
          onClick={submit}
          disabled={busy || email.trim() === '' || name.trim() === ''}
        >
          招待
        </Button>
      </FieldRow>
      <p className="text-xs text-ink-subtle">
        招待した時点でライセンス枠を 1 つ消費します（受諾前でも枠は押さえられます）。
      </p>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </div>
  );
}

/** 発行直後にしか出せない招待リンク。閉じたら二度と表示できない。 */
function InvitationLink({
  email,
  invitation,
  onDismiss,
}: {
  email: string;
  invitation: Invitation;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex flex-col gap-3 border border-brand-700 bg-surface-raised px-6 py-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold">{email} への招待リンク</h2>
        <Button variant="ghost" onClick={onDismiss}>
          閉じる
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-ink-subtle">
        この URL はいま一度だけ表示されます。本人に渡してください。 有効期限は{' '}
        {new Date(invitation.expiresAt * 1000).toLocaleString('ja-JP')} です。
        紛失したら再発行できます。
      </p>
      <div className="flex gap-2">
        <Input readOnly value={invitation.url} className="flex-1 font-mono text-xs" />
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(invitation.url);
            setCopied(true);
          }}
        >
          {copied ? 'コピーしました' : 'コピー'}
        </Button>
      </div>
    </div>
  );
}

/**
 * 招待中は「リンクがまだ生きているか」まで見えないと運用できない。
 * 期限切れの招待は再発行しない限り受諾できないので、別の見た目にする。
 */
function StatusBadge({ member }: { member: Member }) {
  if (member.status === 'active') return <span className="text-ink-subtle">—</span>;
  if (member.status === 'disabled') return <Badge variant="outline">無効</Badge>;

  const expired =
    member.invitationExpiresAt !== null && member.invitationExpiresAt * 1000 <= Date.now();
  return (
    <div className="flex flex-col gap-1">
      <Badge variant={expired ? 'alert' : 'neutral'}>{expired ? '招待期限切れ' : '招待中'}</Badge>
      {member.invitationExpiresAt !== null ? (
        <span className="font-mono text-[10px] text-ink-faint">
          {new Date(member.invitationExpiresAt * 1000).toLocaleDateString('ja-JP')}
          {expired ? ' に失効' : ' まで'}
        </span>
      ) : null}
    </div>
  );
}

function MemberRow({
  member,
  seats,
  defaults,
  expanded,
  onToggle,
  onSaved,
  onReissued,
}: {
  member: Member;
  seats: SeatUsage | undefined;
  defaults: TenantSettings | null;
  expanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
  onReissued: (invitation: Invitation) => void;
}) {
  const [name, setName] = useState(member.name);
  const [role, setRole] = useState<string>(member.role);
  const [hourlyCost, setHourlyCost] = useState(
    member.hourlyCost === null ? '' : String(member.hourlyCost),
  );
  const [hourlyRate, setHourlyRate] = useState(
    member.hourlyRate === null ? '' : String(member.hourlyRate),
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, note?: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      if (note) setMessage(note);
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** 単価は「未設定 = 上位から継承」を隠さずに見せる */
  function inherited(value: number | null, fallback: number | undefined): string {
    if (value !== null) return yenExact(value);
    return fallback === undefined ? '—' : `既定 ${yenExact(fallback)}`;
  }

  return (
    <>
      <tr>
        <Td>
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{member.name}</span>
            <span className="font-mono text-xs text-ink-subtle">{member.email}</span>
          </div>
        </Td>
        <Td className="text-ink-muted">{ROLE_LABEL[member.role] ?? member.role}</Td>
        <Td>
          <StatusBadge member={member} />
        </Td>
        <TdNum className={member.hourlyCost === null ? 'text-ink-subtle' : undefined}>
          {inherited(member.hourlyCost, defaults?.defaultHourlyCost)}
        </TdNum>
        <TdNum className={member.hourlyRate === null ? 'text-ink-subtle' : undefined}>
          {inherited(member.hourlyRate, defaults?.defaultHourlyRate)}
        </TdNum>
        <Td className="text-right">
          <Button onClick={onToggle} aria-expanded={expanded}>
            {expanded ? '閉じる' : '編集'}
          </Button>
        </Td>
      </tr>

      {expanded ? (
        <tr>
          <td colSpan={6} className="border-t border-line bg-surface px-6 py-5">
            <FieldRow>
              <Field label="表示名" className="min-w-48">
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
              </Field>
              <Field label="権限" className="min-w-64">
                <Select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
                  <RoleOptions seats={seats} current={member.role} />
                </Select>
              </Field>
              <Field label="原価単価 (円/時)" className="w-40" hint="空欄で既定値を継承">
                <Input
                  type="number"
                  min={0}
                  step={100}
                  value={hourlyCost}
                  onChange={(e) => setHourlyCost(e.target.value)}
                  className="font-mono"
                  disabled={busy}
                />
              </Field>
              <Field label="売上単価 (円/時)" className="w-40" hint="空欄で既定値を継承">
                <Input
                  type="number"
                  min={0}
                  step={100}
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className="font-mono"
                  disabled={busy}
                />
              </Field>
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  run(
                    () =>
                      api.patch(`/api/users/${member.id}`, {
                        name,
                        role,
                        hourlyCost: hourlyCost === '' ? null : Number(hourlyCost),
                        hourlyRate: hourlyRate === '' ? null : Number(hourlyRate),
                      }),
                    '保存しました',
                  )
                }
              >
                保存
              </Button>
            </FieldRow>

            <div className="flex flex-wrap gap-2 pt-5">
              {member.status === 'invited' ? (
                <>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const result = await api.post<{ invitation: Invitation }>(
                          `/api/users/${member.id}/invitation`,
                        );
                        onReissued(result.invitation);
                      })
                    }
                  >
                    招待リンクを再発行
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => run(() => api.delete(`/api/users/${member.id}`))}
                  >
                    招待を取り消す
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      api.patch(`/api/users/${member.id}`, {
                        status: member.status === 'disabled' ? 'active' : 'disabled',
                      }),
                    )
                  }
                >
                  {member.status === 'disabled' ? '利用を再開する' : '無効化する'}
                </Button>
              )}
            </div>

            {/* この人の予定と実績へ。単価を直した影響を確かめる先でもある */}
            <div className="flex flex-wrap gap-2 pt-5">
              <NavAction target={toAssignments({ query: member.name })}>
                アサイン計画を見る
              </NavAction>
              <NavAction target={toTimeLogs({ query: member.name })}>工数明細を見る</NavAction>
            </div>

            {message ? <p className="pt-4 text-xs text-ink-muted">{message}</p> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
